<template>
  <div class="card p-shadow-app">
    <h1>PATs por Estados Internos</h1>
    <Toast />
    <div class="filter-section p-mb-3">
      <label for="parentStatus">Filtrar por Estado Principal:</label>
      <Dropdown
        id="parentStatus"
        v-model="selectedParentStatus"
        :options="parentStatuses"
        optionLabel="field"
        optionValue="id"
        placeholder="Todos os Estados"
        :showClear="true"
        @change="filterByParentStatus"
        class="w-full p-ml-2"
      />
    </div>
    <div class="p-grid p-fluid dashboard p-jc-center p-mb-3">
      <W4Mstreak
        :states="streakIndicators"
        :loader="loadingIndicators"
        @W4MStreakChangeSelected="onPATClick"
      />
    </div>
    <div style="height: 65vh">
      <DataTable
        :value="patList"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        :paginator="true"
        :rows="20"
        class="p-datatable-sm"
        responsiveLayout="scroll"
        removableSort
        sortMode="single"
        :scrollable="true"
        scrollHeight="flex"
        :autoLayout="true"
        stripedRows
        ref="dt"
        :selection.sync="selectedPats"
        :lazy="true"
        :totalRecords="totalRecords"
        @filter="loadLazyData"
        @sort="loadLazyData"
        @page="loadLazyData"
      >
        <template #header>
          <div class="table-header p-d-flex p-jc-between p-ai-center">
            <span
              class="p-d-flex p-jc-between p-ai-center"
              style="flex-grow: 1"
            >
              <span>
                <Button
                  icon="fa-solid fa-file-csv"
                  label="Exportar CSV"
                  class="p-button-sm p-button-primary p-mr-1"
                  @click="exportCSV($event)"
                />
              </span>
            </span>
          </div>
        </template>
        <template #empty> Nenhum Pat encontrado. </template>
        <template #loading> A carregar... </template>
        <Column
          header="Ações"
          headerClass="p-d-flex p-jc-start"
          bodyClass="p-d-flex p-jc-start"
          v-if="can('managePATs') || can('seePats')"
          :headerStyle="{ 'min-width': '4%', 'max-width': '4%' }"
          :filterHeaderStyle="{ 'min-width': '4%', 'max-width': '4%' }"
          :bodyStyle="{ 'min-width': '4%', 'max-width': '4%' }"
        >
          <template #body="{ data }">
            <Button
              v-if="can('seePats')"
              icon="pi pi-eye"
              class="p-button-rounded p-button-text p-button-success"
              @click="viewPat(data.id)"
            />
          </template>
        </Column>
        <Column
          field="id"
          header="Nopat"
          sortable
          filterField="id"
          :headerStyle="{
            'min-width': '6%',
            'max-width': '6%',
          }"
          :filterHeaderStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :bodyStyle="{ 'min-width': '6%', 'max-width': '6%' }"
          :styles="{ 'min-width': '6%', 'max-width': '6%' }"
        >
          <template #body="slotProps">
            <span class="clickable-item" @click="viewPat(slotProps.data.id)">{{
              slotProps.data.id
            }}</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="oem"
          header="Oem"
          sortable
          filterField="oem"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{
            'min-width': '8%',
            'max-width': '8%',
          }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :styles="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.oem || "" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" @input="filterCallback()" />
          </template>
        </Column>
        <Column
          field="externalRef"
          header="Vref"
          sortable
          filterField="externalRef"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="manufacturerRef"
          header="Ref. Fabricante"
          sortable
          filterField="manufacturerRef"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="technicalName"
          header="Técnico"
          sortable
          filterField="technicalName"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="interventionDate"
          header="Intervenção"
          sortable
          filterField="interventionDate"
          dataType="date"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.interventionDate }}
            {{ slotProps.data.interventionTime }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <v-date-picker
              mode="date"
              v-model="filterModel.value"
              @input="onDateFilterChange($event, filterModel, filterCallback)"
              :masks="{
                input: 'DD-MM-YYYY',
                data: 'DD-MM-YYYY'
              }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="p-column-filter p-inputtext p-component"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
          </template>
        </Column>
        <Column
          field="department"
          header="Centro de assistencia"
          sortable
          filterField="department"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="date"
          header="Pdata"
          sortable
          filterField="date"
          dataType="date"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <v-date-picker
              mode="date"
              v-model="filterModel.value"
              @input="onDateFilterChange($event, filterModel, filterCallback)"
              :masks="{
                input: 'DD-MM-YYYY',
                data: 'DD-MM-YYYY'
              }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="p-column-filter p-inputtext p-component"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
          </template>
        </Column>
        <Column
          field="numberDays"
          header="TAT"
          sortable
          filterField="numberDays"
          :headerStyle="{ 'min-width': '5%', 'max-width': '5%' }"
          :filterHeaderStyle="{ 'min-width': '5%', 'max-width': '5%' }"
          :bodyStyle="{ 'min-width': '5%', 'max-width': '5%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="equipment.brand"
          header="Marca"
          sortable
          filterField="equipment.brand"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="equipment.model"
          header="Maquina"
          sortable
          filterField="equipment.model"
          :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
          :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column field="status" header="Status" sortable filterField="status">
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
              :headerStyle="{ 'min-width': '10%', 'max-width': '10%' }"
              :filterHeaderStyle="{ 'min-width': '10%', 'max-width': '10%' }"
              :bodyStyle="{ 'min-width': '10%', 'max-width': '10%' }"
            />
          </template>
        </Column>
        <Column
          field="client.name"
          header="Loja"
          sortable
          filterField="client.name"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="client.zipCode"
          header="Loja localidade"
          sortable
          filterField="client.zipCode"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="entity.name"
          header="Entidade"
          sortable
          filterField="entity.name"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="entity.zip_code"
          header="Entidade Localidade"
          sortable
          filterField="entity.zip_code"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="type"
          header="Ptipo"
          sortable
          filterField="type"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="publicStatus"
          header="Status Publico"
          sortable
          filterField="publicStatus"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
        <Column
          field="supplierOrderRef"
          header="Ref. Enc. Fornecedor"
          sortable
          filterField="supplierOrderRef"
          :headerStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :filterHeaderStyle="{ 'min-width': '8%', 'max-width': '8%' }"
          :bodyStyle="{ 'min-width': '8%', 'max-width': '8%' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-model="filterModel.value"
              class="p-column-filter"
              @input="filterCallback()"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import W4Mstreak from "@/components/W4Mstreak/W4Mstreak.vue";
import PatService from "@/services/pat.service";
import { FilterMatchMode } from "primevue/api";

export default {
  name: "PATsByInternalStates",
  components: { W4Mstreak },
  data() {
    return {
        selectedParentStatus: null,
        parentStatuses: [],
        internalStates: [],
        groupedStates: [],
        listInternalStates: [],
        loading: false,
        loadingIndicators: false,
        currentStreakIndex: null,
        totalRecords: 0,
        selectedPats: null,
        patList: [],
        streakIndicators: [],
        filters: {
            id: { value: null, matchMode: FilterMatchMode.CONTAINS },
            date: { value: null, matchMode: FilterMatchMode.CONTAINS },
            "equipment.brand": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "equipment.model": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "client.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "client.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
            technicalName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            interventionDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
            interventionTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
            "client.zipCode": { value: null, matchMode: FilterMatchMode.CONTAINS },
            publicStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
            status: { value: null, matchMode: FilterMatchMode.CONTAINS },
            oem: { value: null, matchMode: FilterMatchMode.CONTAINS },
            externalRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
            problem: { value: null, matchMode: FilterMatchMode.CONTAINS },
            numberDays: { value: null, matchMode: FilterMatchMode.EQUALS },
            department: { value: null, matchMode: FilterMatchMode.CONTAINS },
            type: { value: null, matchMode: FilterMatchMode.CONTAINS },
            "entity.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "entity.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "entity.zip_code": { value: null, matchMode: FilterMatchMode.CONTAINS },
            supplierOrderRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
            manufacturerRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
        patDialog: false,
        patForm: {},
        search: "",
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    onDateFilterChange(date, filterModel, filterCallback) {
      if (date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        filterModel.value = `${day}-${month}-${year}`;
      } else {
        filterModel.value = null;
      }

      filterCallback();
    },
    async groupStatesByParent() {
        this.loadingIndicators = true;
        let id = this.selectedParentStatus

        try {
            let response = await PatService.getByInternalStatesParentStatus(id);
            this.streakIndicators = response.map((p) => ({
                title: p.name,
                backgroudColor: p.color || "#e0e0e0",
                textColor: "#000",
                counter: p.pats_count || 0,
                active: false,
                patId: p.id,
            }));

            this.listInternalStates = response;
            this.patList = [];
            this.totalRecords = 0;
        } catch (error) {
            console.error('Error fetching Internal States:', error);
            throw error;
        } finally {
            this.loadingIndicators = false;
        }
    },
    filterByParentStatus() {
      this.groupStatesByParent();
    },
    async fetchParentStatuses() {
      try {
        this.parentStatuses = await PatService.getAllStatus();
        this.selectedParentStatus = this.parentStatuses.length
          ? this.parentStatuses[0].id
          : null;
        await this.groupStatesByParent();
      } catch (error) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Falha ao carregar estados principais.",
          life: 3000,
        });
      }
    },
    resetFilters() {
      this.filters = {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.brand": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.model": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        technicalName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.zipCode": { value: null, matchMode: FilterMatchMode.CONTAINS },
        publicStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS },
        oem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        externalRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        problem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        numberDays: { value: null, matchMode: FilterMatchMode.EQUALS },
        department: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.zip_code": { value: null, matchMode: FilterMatchMode.CONTAINS },
        supplierOrderRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        manufacturerRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    },
    async fetchData() {
      await Promise.all([this.fetchParentStatuses()]);
    },
    async onPATClick(info) {
      this.search = "";
      this.currentStreakIndex = info.currentStateIndex;
      this.currentStatus = info.currentState;

      this.resetFilters();

      await this.loadLazyData({});
    },
    viewPat(patId) {
      let route = this.$router.resolve({
        path: `/pats/${patId}`,
      });
      window.open(route.href, "_blank");
    },
    closeForm() {
      this.patDialog = false;
      this.patForm = {
        clientId: null,
        equipmentId: null,
      };
      return;
    },
    async loadLazyData(event) {
      this.loading = true;

      const page = event.page ? event.page + 1 : 1;

      const params = this.buildQueryParams(page);

      try {
        const response = await PatService.datatableFiltered(params);
        this.patList = response.data;
        this.totalRecords = response.meta.total;
      } catch (error) {
        console.error("Error loading PATs:", error);
        this.$toast.add({
          severity: "error",
          summary: `Erro`,
          detail: `Não foi possível carregar os dados.`,
          life: 3000,
        });
      }

      this.loading = false;
    },
    buildQueryParams(page = 1) {
      const params = new URLSearchParams();

      params.append('page', page);

      if (this.search) {
        params.append('search', this.search);
        return params;
      }

      if (this.selectedParentStatus) {
        params.append('status_id', this.selectedParentStatus);
      }

      if (this.currentStreakIndex !== null) {
        const pat = this.streakIndicators[this.currentStreakIndex];
        params.append('internal_state_id', pat.patId);
      }

      for (const [key, filter] of Object.entries(this.filters)) {
        if (filter.value !== null && filter.value !== '') {
          params.append(`filters[${key}]`, filter.value);
        }
      }

      return params;
    },
    async exportCSV() {
      this.loading = true;
      try {
        const response = await PatService.exportCSV(this.buildQueryParams());
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pats_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error("Error exporting CSV:", error);
        this.$toast.add({
          severity: "error",
          summary: `Erro`,
          detail: `Não foi possível exportar os dados.`,
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.text-blue {
  color: #007bff;
  cursor: help;
}
</style>
